import { motion, useReducedMotion } from "motion/react";
import { ChevronRight, Mail } from "lucide-react";
import { FaInstagram, FaTelegramPlane, FaYoutube } from "react-icons/fa";
import type { ComponentType } from "react";
import { cn } from "@/lib/utils";

type Card = {
  icon: ComponentType<{ className?: string }>;
  glow: string;
  iconColor: string;
  label: string;
  value: string;
  href: string;
  isMailto?: boolean;
  aria: string;
};

const CARDS: Card[] = [
  {
    icon: FaInstagram,
    glow: "bg-[radial-gradient(circle_at_30%_30%,oklch(0.72_0.2_350/45%),oklch(0.6_0.22_300/35%))]",
    iconColor: "text-[oklch(0.78_0.17_350)]",
    label: "Instagram",
    value: "@THEQUANTUMMINDSOFFICIAL",
    href: "https://instagram.com/THEQUANTUMMINDSOFFICIAL",
    aria: "Open Instagram profile @THEQUANTUMMINDSOFFICIAL",
  },
  {
    icon: FaYoutube,
    glow: "bg-[radial-gradient(circle_at_30%_30%,oklch(0.65_0.23_25/45%),oklch(0.55_0.2_20/30%))]",
    iconColor: "text-[oklch(0.72_0.2_25)]",
    label: "YouTube",
    value: "@THEQUANTUMMINDSOFFICIAL",
    href: "https://youtube.com/@THEQUANTUMMINDSOFFICIAL",
    aria: "Open YouTube channel @THEQUANTUMMINDSOFFICIAL",
  },
  {
    icon: FaTelegramPlane,
    glow: "bg-[radial-gradient(circle_at_30%_30%,oklch(0.7_0.16_240/45%),oklch(0.6_0.16_250/30%))]",
    iconColor: "text-[oklch(0.76_0.14_240)]",
    label: "Telegram",
    value: "@thequantummindsofficialbot",
    href: "https://t.me/thequantummindsofficialbot",
    aria: "Open Telegram support bot @thequantummindsofficialbot",
  },
  {
    icon: Mail,
    glow: "bg-[radial-gradient(circle_at_30%_30%,oklch(0.9_0.03_250/40%),oklch(0.7_0.1_250/28%))]",
    iconColor: "text-foreground",
    label: "Email",
    value: "thequantummindsofficial@gmail.com",
    href: "mailto:thequantummindsofficial@gmail.com",
    isMailto: true,
    aria: "Send an email to thequantummindsofficial@gmail.com",
  },
];

function SocialConnectCard({ card }: { card: Card }) {
  const reduce = useReducedMotion();
  const Icon = card.icon;
  return (
    <motion.a
      href={card.href}
      aria-label={card.aria}
      {...(card.isMailto ? {} : { target: "_blank", rel: "noopener noreferrer" })}
      whileTap={reduce ? undefined : { scale: 0.98 }}
      transition={{ type: "spring", stiffness: 480, damping: 26 }}
      className={cn(
        "glass group flex min-w-0 cursor-pointer items-center gap-4 rounded-3xl p-5",
        "transition-[transform,background-color,border-color,opacity] duration-200 ease-out",
        "hover:border-border hover:bg-foreground/[0.04] motion-safe:hover:-translate-y-0.5",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      )}
    >
      <span className="relative flex size-11 shrink-0 items-center justify-center rounded-full border border-hairline bg-surface">
        <span
          aria-hidden
          className={cn("absolute inset-0 rounded-full blur-md opacity-70", card.glow)}
        />
        <Icon className={cn("relative size-5", card.iconColor)} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {card.label}
        </span>
        <span className="mt-0.5 block truncate text-sm font-medium text-foreground">
          {card.value}
        </span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-active:translate-x-0.5" />
    </motion.a>
  );
}

export function SocialConnectSection() {
  return (
    <section
      className="mt-4"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}
    >
      <h2 className="text-sm font-semibold">Connect With The Quantum Minds</h2>
      <p className="mt-1 text-xs leading-snug text-muted-foreground">
        Stay connected for updates, study content, announcements and support.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {CARDS.map((card) => (
          <SocialConnectCard key={card.label} card={card} />
        ))}
      </div>
    </section>
  );
}